import type { TrafficTopRow } from '@bartizo/shared';

/**
 * How worrying one top-list row looks, from what that row already carries: its
 * requests, its refused (4xx) and failed (5xx) answers, and its visitor class.
 * Rule 19 means there is nothing else to go on, so this is a reading of ratios,
 * not a verdict, and the page says which ratio made it.
 *
 * The thresholds, chosen so ordinary browsing stays `none`:
 *
 * - A real page answers almost everything with 2xx or 3xx. A row whose answers
 *   are mostly refusals is something asking for what is not there or not
 *   allowed: scanners probing `/.env` and `/wp-login.php` get 404 and 403.
 *   `critical` at 90% refused over at least 20 requests, `high` at 50%,
 *   `medium` at 20%.
 * - Server errors are worse news per request than refusals: one in ten is
 *   `high`, one in twenty `medium`.
 * - A bot that claimed to be a search engine and was not (`fake_bot`) is `high`
 *   whatever its ratios.
 * - Under 5 requests nothing is more than `low`: a ratio of two is noise.
 */

export type TrafficRisk = 'none' | 'low' | 'medium' | 'high' | 'critical';

export interface TrafficRiskReading {
  risk: TrafficRisk;
  refusedShare: number;
  errorShare: number;
  /** One line on what made it, for a tooltip or a "why" column. */
  why: string;
}

const MIN_REQUESTS = 5;
const CRITICAL_MIN_REQUESTS = 20;

const ORDER: readonly TrafficRisk[] = ['none', 'low', 'medium', 'high', 'critical'];
const worse = (a: TrafficRisk, b: TrafficRisk): TrafficRisk =>
  ORDER.indexOf(a) >= ORDER.indexOf(b) ? a : b;

const pct = (share: number): string => `${String(Math.round(share * 100))}%`;

export function trafficRisk(
  row: Pick<TrafficTopRow, 'requests' | 'status4xx' | 'status5xx' | 'botCategory'>,
): TrafficRiskReading {
  const refusedShare = row.requests > 0 ? row.status4xx / row.requests : 0;
  const errorShare = row.requests > 0 ? row.status5xx / row.requests : 0;

  if (row.requests < MIN_REQUESTS) {
    const risk: TrafficRisk = row.status4xx + row.status5xx > 0 ? 'low' : 'none';
    return { risk, refusedShare, errorShare, why: 'Too few requests to read anything into' };
  }

  let risk: TrafficRisk = 'none';
  const reasons: string[] = [];

  const refusedRisk: TrafficRisk =
    refusedShare >= 0.9 && row.requests >= CRITICAL_MIN_REQUESTS
      ? 'critical'
      : refusedShare >= 0.5
        ? 'high'
        : refusedShare >= 0.2
          ? 'medium'
          : refusedShare > 0
            ? 'low'
            : 'none';
  if (refusedRisk !== 'none') reasons.push(`${pct(refusedShare)} refused`);
  risk = worse(risk, refusedRisk);

  const errorRisk: TrafficRisk =
    errorShare >= 0.1 ? 'high' : errorShare >= 0.05 ? 'medium' : errorShare > 0 ? 'low' : 'none';
  if (errorRisk !== 'none') reasons.push(`${pct(errorShare)} server errors`);
  risk = worse(risk, errorRisk);

  if (row.botCategory === 'fake_bot') {
    reasons.push('claims to be a search engine and is not');
    risk = worse(risk, 'high');
  }

  return {
    risk,
    refusedShare,
    errorShare,
    why: reasons.length > 0 ? reasons.join(', ') : 'Answered normally',
  };
}
